import { firebaseDb } from '../services/firebase'
import {
  Collection,
  PaginationLimit,
  OrderStatus,
  OrderItemStatus
} from '../config/enums'
import { Order, orderConverter } from '../models/Order'

export type OrderChangeHandler = (orders: Order[]) => void

export interface FilterOption {
  field: string
  value: string
}

export default class OrderRepo {
  private vendorId: string
  private ref = firebaseDb.collection(Collection.orders)
  private orderDocs: firebase.firestore.QueryDocumentSnapshot<Order>[] = []
  private validStatuses = [OrderStatus.approved, OrderStatus.closed]

  constructor(vendorId: string) {
    this.vendorId = vendorId
  }

  private paginatedOrdersQuery(
    limit: number = PaginationLimit.tables,
    after?: Order,
    before?: Order
  ) {
    let query = this.ref
      .where('status', 'in', this.validStatuses)
      .where('vendorIds', 'array-contains', this.vendorId)
      .withConverter(orderConverter)
      .orderBy('createdAt', 'desc')

    if (after || before) {
      const searchId = after ? after.id : before!.id
      const filtered = this.orderDocs.filter(doc => {
        return doc.data().id === searchId
      })

      if (filtered.length) {
        if (after) {
          query = query.startAfter(filtered[0]).limit(limit)
        } else {
          query = query.endBefore(filtered[0]).limitToLast(limit)
        }
      } else {
        query = query.limit(limit)
      }
    } else {
      query = query.limit(limit)
    }

    return query
  }

  async fetchOrders(
    limit: number = PaginationLimit.tables,
    after?: Order,
    before?: Order,
    status?: OrderItemStatus,
    orderNumber?: string,
    customerName?: string
  ) {
    let docs: firebase.firestore.QueryDocumentSnapshot<Order>[] = []

    if (status) {
      // Filter by status by fetching last 100 records, then filtering out
      // orders with any order item matching the status
      const res = await this.paginatedOrdersQuery(100).get()
      const filteredDocs: firebase.firestore.QueryDocumentSnapshot<Order>[] = []
      res.docs.forEach(doc => {
        const order = doc.data()
        let statusMatched = false
        for (let i = 0; i < order.orderItems.length; i++) {
          if (
            order.orderItems[i].vendor.id === this.vendorId &&
            order.orderItems[i].status === status
          ) {
            statusMatched = true
            break
          }
        }
        if (statusMatched) {
          filteredDocs.push(doc)
        }
      })
      docs = filteredDocs
    } else if (orderNumber) {
      // For filterting by order number
      const res = await this.paginatedOrdersQuery(limit, after, before)
        .where('orderNumber', '==', orderNumber)
        .get()
      docs = res.docs
    } else if (customerName) {
      // Create multiple queries in able to do a logical "OR" query
      // For filtering by customer firstName or lastName (or both)
      let names = customerName.split(' ')
      if (names.length > 1) {
        names.push(customerName)
      }

      const promises: Promise<any>[] = []
      names.forEach(name => {
        promises.push(
          this.paginatedOrdersQuery(limit, after, before)
            .where('biller.firstName', '==', name)
            .get()
        )
        promises.push(
          this.paginatedOrdersQuery(limit, after, before)
            .where('biller.lastName', '==', name)
            .get()
        )
      })

      const results = await Promise.all(promises)
      results.forEach(res => {
        if (!res.empty) {
          docs = [...docs, ...res.docs]
        }
      })
    } else {
      // Get all orders without filter
      docs = (await this.paginatedOrdersQuery(limit, after, before).get()).docs
    }

    // Filter unique order documents
    this.orderDocs = docs.filter(
      (doc, i, docsLeft) => i === docsLeft.findIndex(d => d.id === doc.id)
    )

    const orders: Order[] = []
    this.orderDocs.forEach(doc => {
      orders.push(doc.data())
    })

    return orders
  }
}
